import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getOrderById, cancelOrder } from "../../../api/orderService";
import OrderTimeline from "../../../components/orders/OrderTimeline";

import {
    PageHeader,
    Panel,
    InfoRow,
    PrimaryButton,
    DarkButton,
} from "../../../components/ui/AdminUI";

import StatusBadge from "../../../components/ui/StatusBadge";
import { DangerButton } from "../../../components/ui/FormControls";

export default function ClientOrderDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [order, setOrder] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);

    useEffect(() => {
        getOrderById(id)
            .then(setOrder)
            .catch((err) =>
                setError(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Failed to load order."
                )
            )
            .finally(() => setLoading(false));
    }, [id]);

    const handleCancel = async () => {
        if (!window.confirm("Cancel this order?")) return;

        try {
            setCancelling(true);
            await cancelOrder(id);
            navigate("/client/orders");
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to cancel order."
            );
        } finally {
            setCancelling(false);
        }
    };

    if (loading) return <div className="p-6 text-slate-600">Loading order...</div>;

    if (error) {
        return (
            <div className="rounded-xl border border-red-300 bg-red-50 p-5 text-red-700">
                {error}
            </div>
        );
    }

    if (!order) {
        return (
            <Panel title="Order">
                <p className="text-slate-500">Order not found.</p>
            </Panel>
        );
    }

    const status = order.shipment?.status || order.status || "Created";
    const paymentStatus = order.paymentStatus || "Pending";
    const canCancel = ["Created", "Confirmed"].includes(status);

    return (
        <div className="space-y-6">
            <PageHeader
                title={`Order #${order.orderNumber || order.id}`}
                subtitle="Order status, payment information and shipment timeline."
                action={
                    <DarkButton onClick={() => navigate("/client/orders")}>
                        Back to Orders
                    </DarkButton>
                }
            />

            <Panel title="Order Timeline" subtitle="Current logistics progress.">
                <OrderTimeline status={status} />
            </Panel>

            <Panel title="Order Information" subtitle="Main order details.">
                <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                    <InfoRow label="Order ID" value={order.id} />
                    <InfoRow label="Order Number" value={order.orderNumber || "—"} />
                    <InfoRow
                        label="Amount"
                        value={`${order.totalAmount ?? "—"} ${order.currency || ""}`}
                    />
                    <InfoRow label="Source" value={order.source || "Web"} />
                    <InfoRow label="External Order" value={order.externalOrderNumber || "—"} />
                    <InfoRow label="Shipment ID" value={order.shipmentId || "—"} />
                    <InfoRow label="Status" value={<StatusBadge status={status} />} />
                    <InfoRow label="Payment" value={<StatusBadge status={paymentStatus} />} />
                </div>
            </Panel>

            <Panel title="Actions" subtitle="Available operations for this order.">
                <div className="flex flex-wrap gap-3">
                    {paymentStatus !== "Completed" && order.shipmentId && (
                        <PrimaryButton
                            onClick={() =>
                                navigate(`/payments/create/${order.shipmentId}`)
                            }
                        >
                            Pay Order
                        </PrimaryButton>
                    )}

                    {canCancel && (
                        <DangerButton disabled={cancelling} onClick={handleCancel}>
                            {cancelling ? "Cancelling..." : "Cancel Order"}
                        </DangerButton>
                    )}

                    <DarkButton onClick={() => navigate("/client/shipments")}>
                        My Shipments
                    </DarkButton>
                </div>
            </Panel>
        </div>
    );
}